import React from 'react';
import { RadioButton } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { addMinutes, parseDateInTimeZone, timeZone } from '@brighterion/utils/date-timezone';
import { DAY_IN_MINUTES, LIST_ENTRY_DATE_FORMAT } from '@brighterion/utils/constants';
import { Field, FieldProps, FormikProps } from 'formik';
import { GeneralListEntry, ListEntryApiResponseObj, PanListEntry, RangeListEntry } from '@brighterion/types/listEntry';
import DatePickerWrapper from '../DatePickerWrapper/DatePickerWrapper';
import styles from './EndDateTimePicker.css';

type Props = {
    formikProps: FormikProps<GeneralListEntry | PanListEntry | RangeListEntry | ListEntryApiResponseObj>;
};

const EndDateTimePicker = ({ formikProps }: Props) => {
    const { t } = useTranslation();
    const { initialValues, values, setFieldValue, isSubmitting } = formikProps;
    const [noEndTime, setNoEndTime] = React.useState(true);

    React.useEffect(() => {
        if (formikProps?.values?.endTime) {
            setNoEndTime(false);
        } else {
            setNoEndTime(true);
        }
    }, []);

    const isInitialStartTimePast = new Date(initialValues.startTime).getTime() <= new Date().getTime();
    return (
        <div
            data-testid='select-endTime-content'
            role='radiogroup'
            aria-labelledby='select-endTime-option'
            className={styles.radioGroup}
        >
            <div id='select-endTime-option' className={styles.radioButtonGroupLabel}>
                {t('End Date and Time')}
            </div>
            <RadioButton
                id='endTime-none'
                dataTestId='endTime-none'
                text='None'
                name='endTime-none-option'
                value={noEndTime ? 'true' : 'false'}
                onChange={() => {
                    setNoEndTime(true);
                    setFieldValue('endTime', '');
                }}
                aria-labelledby='endTime-none'
                className={styles.radioButton}
                checked={noEndTime}
                disabled={isSubmitting}
            />

            <RadioButton
                dataTestId='endTime-custom'
                id='endtime-custom'
                text='Custom'
                name='endTime-custom-option'
                value={noEndTime ? 'false' : 'true'}
                onChange={() => {
                    setNoEndTime(false);
                    setFieldValue(
                        'endTime',
                        parseDateInTimeZone(
                            values.startTime && !isInitialStartTimePast
                                ? addMinutes(new Date(values.startTime), DAY_IN_MINUTES)
                                : addMinutes(new Date(), DAY_IN_MINUTES),
                            timeZone,
                            LIST_ENTRY_DATE_FORMAT
                        )
                    );
                }}
                aria-labelledby='endTime-custom'
                className={styles.radioButton}
                checked={!noEndTime}
                disabled={isSubmitting}
            />

            {!noEndTime && (
                <Field name='endTime'>
                    {({ field, form: { errors } }: FieldProps) => {
                        return (
                            <div className={styles.endTimeContainer}>
                                <DatePickerWrapper
                                    selected={new Date(field.value)}
                                    onChange={(date: any) => setFieldValue('endTime', date)}
                                    disabled={isSubmitting}
                                    showIcon
                                />
                                {errors[field.name] && <div className={styles.labelError}>{errors[field.name]}</div>}
                            </div>
                        );
                    }}
                </Field>
            )}
        </div>
    );
};

export default EndDateTimePicker;
